import { Badge, confirm, DataTable, type TanstackReactTable } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { EditIcon, EyeOffIcon, TrashIcon } from "lucide-react";
import { AppImage } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import type { ShopProductSkuListItem } from "../api/client";

type ShopProductsTableProps = {
  canUpdate: boolean;
  canDelete: boolean;
  isLoading: boolean;
  data: ShopProductSkuListItem[];
  offset: number;
  limit: number;
  totalCount: number;
  onEdit: (row: ShopProductSkuListItem) => void;
  onDeactivate: (shopProductSkuId: string) => Promise<void>;
  onRemove: (shopProductSkuId: string) => Promise<void>;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function ShopProductsTable({
  canUpdate,
  canDelete,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onEdit,
  onDeactivate,
  onRemove,
  onPaginationChange,
}: ShopProductsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ShopProductSkuListItem>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    {
      id: "product",
      header: "ສິນຄ້າ",
      cell: ({ row }) => {
        const { thumbnailUrl, productName, catalogProductId, oemPartNo } =
          row.original;
        const thumb = !thumbnailUrl?.trim() ? (
          <div className="flex size-12 shrink-0 items-center justify-center rounded border border-muted-foreground/30 border-dashed bg-muted/20 text-muted-foreground text-xs">
            —
          </div>
        ) : (
          <AppImage
            src={thumbnailUrl}
            alt=""
            className="size-12 shrink-0 rounded border border-border/60 object-cover"
            fit="cover"
          />
        );
        return (
          <div className="flex items-center gap-3">
            {thumb}
            <div className="min-w-0 flex-1 space-y-0.5">
              <Link
                to="/app/catalog/$id"
                params={{ id: catalogProductId }}
                className="block truncate text-primary hover:underline"
              >
                {productName}
              </Link>
              <p className="truncate text-muted-foreground text-xs">
                {oemPartNo ?? "—"}
              </p>
            </div>
          </div>
        );
      },
      size: 260,
    },
    {
      id: "sku",
      header: "SKU ຂອງຮ້ານ",
      cell: ({ row }) => row.original.sku,
      size: 120,
    },
    {
      id: "price",
      header: "ລາຄາ",
      cell: ({ row }) => Number(row.original.price).toLocaleString("lo-LA"),
      size: 100,
    },
    {
      id: "currency",
      header: "ສະກຸນເງິນ",
      cell: ({ row }) => row.original.currency,
      size: 80,
    },
    {
      id: "barcode",
      header: "Barcode",
      cell: ({ row }) => row.original.barcode ?? "—",
      size: 120,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="success">ໃຊ້ງານ</Badge>
        ) : (
          <Badge variant="destructive">ປິດ</Badge>
        ),
      size: 90,
    },
    {
      id: "updatedAt",
      header: "ວັນທີ່ອັບເດດ",
      cell: ({ row }) => format(new Date(row.original.updatedAt), "dd/MM/yyyy"),
      size: 110,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const r = row.original;
        const actions = [];

        if (canUpdate) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(r),
          });
        }

        if (canUpdate && r.isActive) {
          actions.push({
            label: "ປິດการขาย",
            icon: <EyeOffIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ປິດการขาย SKU",
                description:
                  "SKU จะไม่แสดงใน POS/ร้าน แต่ยังเก็บข้อมูลไว้ (listing active ต้อง unpublish ก่อน)",
                actionText: "ປິດการขาย",
              });
              if (ok) await onDeactivate(r.id);
            },
          });
        }

        if (canDelete) {
          actions.push({
            label: "ລຶບອອກຈາກຮ້ານ",
            variant: "destructive" as const,
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບອອກຈາກຮ້ານ",
                description:
                  "ລຶບ SKU, listing ແລະ ສະຕ໋ອກອອກຈາກຮ້ານ permanently — ບໍ່ສາມາດກູ້ຄືນໄດ້. ຖ້າມີປະຫວັດຂາຍ/ຮັບສິນຄ້າ ຈະລຶບບໍ່ໄດ້.",
                actionText: "ລຶບອອກຈາກຮ້ານ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onRemove(r.id);
            },
          });
        }

        if (actions.length === 0) return null;
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 100,
    },
  ];

  return (
    <DataTable<ShopProductSkuListItem, unknown>
      noDataMessage="ບໍ່ພົບສິນຄ້າຂອງຮ້ານ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
